'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'hooks/dom/useIdleTimer';
import { analytics } from 'lib/analytics/analytics';
import { allowsDataLayer } from 'lib/consent/gate';
import {
  DEFAULT_IDLE_POPUP_DELAY_MS,
  type IdlePopupControls,
  type IdlePopupEvent,
  type IdlePopupTrackingData,
  type UseIdlePopupOptions,
} from './constants';
import { isBlockingModalOpen } from './isBlockingModalOpen';

const pushToDataLayer = (event: string, data?: IdlePopupTrackingData): void => {
  if (!allowsDataLayer()) return;
  try {
    (
      window as Window & { dataLayer?: Record<string, unknown>[] }
    ).dataLayer?.push({ event, ...data });
  } catch {
    // ignore
  }
};

const getDeviceType = (): string => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

const writeFlag = (key: string): void => {
  try {
    localStorage.setItem(key, 'true');
  } catch {
    // ignore
  }
};

const MODAL_POLL_MS = 300;

/**
 * Shows a popup once the user has been on the page for `delayMs`. Dismissal and
 * completion persist in localStorage, so it never re-appears across sessions.
 * If another modal is open when the timer fires, the popup waits until that
 * modal closes instead of stacking on top of it.
 */
export function useIdlePopup({
  storageKeys,
  tracking,
  delayMs = DEFAULT_IDLE_POPUP_DELAY_MS,
  inactivityMs,
  trackingData,
  enabled = true,
}: UseIdlePopupOptions): IdlePopupControls {
  const [isVisible, setIsVisible] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const waitRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasTrackedMount = useRef(false);
  const trackingDataRef = useRef(trackingData);
  const { dismissed: dismissedKey, completed: completedKey } = storageKeys;

  const clearScheduled = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (waitRef.current) {
      clearInterval(waitRef.current);
      waitRef.current = null;
    }
  }, []);

  useEffect(() => {
    trackingDataRef.current = trackingData;
  }, [trackingData]);

  const track = useCallback(
    (event: IdlePopupEvent, extra?: IdlePopupTrackingData) => {
      const data = {
        ...trackingDataRef.current,
        page: window.location.pathname,
        device: getDeviceType(),
        ...extra,
      };
      analytics.track(tracking.analytics[event], data);
      pushToDataLayer(tracking.gtm[event], data);
    },
    [tracking],
  );

  // Inactivity mode: armed only once the dismissed/completed checks pass.
  const [inactivityArmed, setInactivityArmed] = useState(false);
  const useInactivity = inactivityMs !== undefined;

  useIdleTimer({
    timeoutMs: inactivityMs ?? delayMs,
    enabled: useInactivity && inactivityArmed,
    onIdle: () => {
      setInactivityArmed(false);
      setIsVisible(true);
      track('POPUP_SHOWN', { delayMs, inactivityMs, reason: 'idle' });
    },
  });

  useEffect(() => {
    if (!enabled) {
      setInactivityArmed(false);
      clearScheduled();
      return;
    }

    try {
      const wasCompleted = localStorage.getItem(completedKey);
      const wasDismissed = localStorage.getItem(dismissedKey);

      if (wasCompleted || wasDismissed) {
        if (!hasTrackedMount.current) {
          hasTrackedMount.current = true;
          track('SUPPRESSED', {
            delayMs,
            reason: wasCompleted ? 'completed' : 'dismissed',
          });
        }
        return;
      }
    } catch {
      // localStorage unavailable (privacy mode) — proceed
    }

    // Guard against Strict Mode double-mount
    if (!hasTrackedMount.current) {
      hasTrackedMount.current = true;
      track('TIMER_STARTED', useInactivity ? { delayMs, inactivityMs } : { delayMs });
    }

    if (useInactivity) {
      setInactivityArmed(true);
      return () => setInactivityArmed(false);
    }

    const show = () => {
      clearScheduled();
      setIsVisible(true);
      track('POPUP_SHOWN', { delayMs });
    };

    timerRef.current = setTimeout(() => {
      if (!isBlockingModalOpen()) {
        show();
        return;
      }
      waitRef.current = setInterval(() => {
        if (!isBlockingModalOpen()) show();
      }, MODAL_POLL_MS);
    }, delayMs);

    return () => {
      clearScheduled();
      setInactivityArmed(false);
    };
  }, [
    delayMs,
    inactivityMs,
    useInactivity,
    enabled,
    dismissedKey,
    completedKey,
    track,
    clearScheduled,
  ]);

  const dismiss = useCallback(() => {
    setIsVisible(false);
    track('POPUP_DISMISSED');
    writeFlag(dismissedKey);
  }, [track, dismissedKey]);

  const complete = useCallback(() => {
    setIsVisible(false);
    track('COMPLETED');
    writeFlag(completedKey);
    writeFlag(dismissedKey);
  }, [track, completedKey, dismissedKey]);

  const cancel = useCallback(() => {
    clearScheduled();
    setInactivityArmed(false);
    setIsVisible(false);
    track('CANCELLED');
  }, [track, clearScheduled]);

  return { isVisible, cancel, dismiss, complete };
}
